/**
 * Sélecteur de coloris entre produits distincts.
 *
 * Chaque coloris de Cadence est un produit séparé, pour que la galerie
 * ne montre que les photos du coloris consulté. Ce composant rétablit
 * le lien entre eux : il interroge la recherche filtrée par le tag de
 * groupe et affiche une pastille cliquable par coloris frère.
 *
 * Deux emplacements : la fiche produit (pastilles larges, avec libellé)
 * et la carte produit en grille (pastilles compactes, sans libellé).
 */

/**
 * Une page de collection affiche plusieurs cartes du même modèle — trois
 * cartes Colette déclencheraient trois fois la même requête. On mémorise
 * la promesse par adresse pour n'interroger la boutique qu'une fois par
 * modèle et par page.
 */
const requetesEnCours = new Map();

/**
 * Les coloris d'un modèle ne changent pas d'une page à l'autre. On garde donc
 * la réponse dans sessionStorage : au deuxième chargement, les pastilles
 * s'affichent immédiatement au lieu d'attendre un aller-retour réseau.
 * La durée de vie reste courte — un onglet — pour ne pas montrer un coloris
 * retiré du catalogue.
 */
// Suffixe de version : à changer quand les noms affichés changent, pour que
// les onglets déjà ouverts ne montrent pas l'ancien nom.
const CLE_CACHE = 'cadence:coloris:v2:';

function lireCache(endpoint) {
  try {
    const brut = sessionStorage.getItem(CLE_CACHE + endpoint);
    if (!brut) return null;
    const { produits, horodatage } = JSON.parse(brut);
    // Dix minutes : assez pour une visite, trop court pour figer une erreur.
    if (Date.now() - horodatage > 600000) return null;
    return produits;
  } catch (erreur) {
    return null;
  }
}

function ecrireCache(endpoint, produits) {
  try {
    sessionStorage.setItem(
      CLE_CACHE + endpoint,
      JSON.stringify({ produits, horodatage: Date.now() })
    );
  } catch (erreur) {
    // Quota plein ou navigation privée : le cache est un confort, on continue.
  }
}

function chargerGroupe(endpoint) {
  if (!requetesEnCours.has(endpoint)) {
    const enCache = lireCache(endpoint);
    if (enCache) {
      requetesEnCours.set(endpoint, Promise.resolve(enCache));
      return requetesEnCours.get(endpoint);
    }

    const promesse = fetch(endpoint, { headers: { Accept: 'application/json' } })
      .then((reponse) => {
        if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
        return reponse.text();
      })
      .then((texte) => {
        const produits = JSON.parse(texte.trim());
        if (!Array.isArray(produits)) throw new Error('réponse inattendue');
        ecrireCache(endpoint, produits);
        return produits;
      });
    requetesEnCours.set(endpoint, promesse);
  }
  return requetesEnCours.get(endpoint);
}

/**
 * Prépare la bascule vers un coloris avant même le clic.
 *
 * Le navigateur télécharge la page du coloris pointé et sa photo pendant que
 * la cliente hésite. Au clic, la navigation part d'un cache chaud : le
 * changement paraît instantané là où il fallait auparavant attendre un
 * chargement complet.
 */
const dejaPrepares = new Set();

function preparer(produit) {
  if (!produit || dejaPrepares.has(produit.handle)) return;
  dejaPrepares.add(produit.handle);

  if (produit.url) {
    const lien = document.createElement('link');
    lien.rel = 'prefetch';
    lien.as = 'document';
    lien.href = produit.url;
    document.head.appendChild(lien);
  }

  const source = produit.image_large || produit.image;
  if (source) {
    const image = document.createElement('link');
    image.rel = 'preload';
    image.as = 'image';
    image.href = source;
    document.head.appendChild(image);
  }
}

class ColorSiblings extends HTMLElement {
  connectedCallback() {
    if (this.dataset.charge === 'oui') return;
    this.dataset.charge = 'oui';
    this.liste = this.querySelector('.color-siblings__list');
    this.compact = this.hasAttribute('data-compact');
    this.charger();
  }

  async charger() {
    try {
      const produits = await chargerGroupe(this.dataset.endpoint);
      // Un coloris seul n'est pas un choix : on n'affiche rien.
      if (produits.length < 2) {
        this.hidden = true;
        return;
      }
      this.rendre(produits);
    } catch (erreur) {
      // Le sélecteur est un confort, pas un passage obligé :
      // en cas d'échec on disparaît sans casser la page.
      console.warn('[color-siblings] chargement impossible :', erreur);
      this.hidden = true;
    }
  }

  rendre(produits) {
    const actuel = this.dataset.actuel;

    this.liste.replaceChildren(
      ...produits.map((produit) => {
        const estActuel = produit.handle === actuel;
        const noeud = document.createElement(estActuel ? 'span' : 'a');

        noeud.className =
          'color-siblings__item' +
          (estActuel ? ' color-siblings__item--actuel' : '') +
          (produit.disponible ? '' : ' color-siblings__item--epuise');
        noeud.title = produit.disponible
          ? produit.couleur
          : produit.couleur + ' — épuisé';

        if (estActuel) {
          noeud.setAttribute('aria-current', 'true');
        } else {
          noeud.href = produit.url;
          noeud.setAttribute('aria-label', 'Voir le coloris ' + produit.couleur);

          /*
           * Sur écran tactile, une pastille de carte ne doit pas emmener
           * directement sur la fiche : au doigt, on veut d'abord regarder le
           * coloris. Le premier appui remplace donc la photo de la carte, et
           * c'est la carte elle-même qui mène ensuite au bon produit.
           * Au clavier et à la souris, le lien reste un lien.
           */
          if (this.compact && matchMedia('(hover: none)').matches) {
            noeud.addEventListener('click', (evenement) => {
              evenement.preventDefault();
              this.epingler(produit, produits);
            });
          }

          /*
           * À la souris, survoler une pastille doit montrer CE coloris.
           * Sans cela, la règle de survol de la carte prend le dessus et
           * affiche la seconde photo du coloris déjà affiché — ce qui n’a
           * aucun sens quand on pointe une autre couleur.
           */
          if (this.compact && matchMedia('(hover: hover)').matches) {
            noeud.addEventListener('mouseenter', () => this.previsualiser(produit));
            noeud.addEventListener('mouseleave', () => this.restaurer());
            noeud.addEventListener('focus', () => this.previsualiser(produit));
            noeud.addEventListener('blur', () => this.restaurer());
          }

          /*
           * On prépare la bascule dès que l'intention se manifeste : survol à
           * la souris, premier contact au doigt. Le téléchargement se fait
           * pendant l'hésitation, pas après la décision.
           */
          noeud.addEventListener('pointerenter', () => preparer(produit), { once: true });
          noeud.addEventListener('touchstart', () => preparer(produit), {
            once: true,
            passive: true,
          });
        }

        if (produit.image) {
          const img = document.createElement('img');
          img.alt = '';
          img.loading = 'lazy';
          /*
           * Sur la fiche, la vignette ne mesure que quelques dizaines de
           * pixels : la version de 800 px pesait cinq fois plus que
           * nécessaire. On propose plusieurs largeurs et le navigateur prend
           * celle qui correspond à la place réellement occupée.
           */
          if (!this.compact && produit.image_large) {
            img.sizes = 'auto, 120px';
            img.srcset = [120, 180, 240, 360, 480, 800]
              .map((largeur) => produit.image_large.replace(/width=\d+/, 'width=' + largeur) + ' ' + largeur + 'w')
              .join(', ');
          }
          img.src = this.compact ? produit.image : produit.image_large || produit.image;
          /*
           * Dimensions declarees pour que le navigateur reserve la place avant
           * meme d'avoir la photo. La feuille de style impose de toute facon la
           * taille finale — inline-size 100 % dans les deux cas — on reprend
           * donc simplement le rapport qu'elle applique : carre en pastille
           * compacte, 3/4 sur la fiche produit.
           */
          if (this.compact) {
            img.width = 160;
            img.height = 160;
          } else {
            img.width = 800;
            img.height = 1067;
          }
          noeud.appendChild(img);
        } else {
          noeud.textContent = (produit.couleur || '?').slice(0, 2);
        }

        /*
         * Sur la fiche produit, le nom du coloris s'écrit sous sa vignette :
         * une cliente ne devine pas « Ristretto » à la couleur seule, et
         * survoler chaque pastille pour lire une infobulle est un mauvais
         * service. En grille, la place manque : l'infobulle suffit.
         */
        if (!this.compact && produit.couleur) {
          const nom = document.createElement('span');
          nom.className = 'color-siblings__nom';
          nom.textContent = produit.couleur;
          noeud.appendChild(nom);
        }

        return noeud;
      })
    );

    // La carte ne réserve pas de place tant que les pastilles n'existent
    // pas : on ne révèle qu'une fois le contenu en place, sinon la grille
    // sursaute au chargement.
    this.dataset.pret = 'oui';
  }

  /** Survol d’une pastille : montre le coloris pointé, prioritaire sur la carte. */
  previsualiser(produit) {
    const carte = this.closest("product-card");
    if (!carte || !window.CadenceApercu) return;
    window.CadenceApercu.montrer(
      carte,
      produit.image_large || produit.image,
      window.CadenceApercu.NIVEAU_PASTILLE
    );
  }

  /** Sortie du survol : rend la main au coloris épinglé, ou au survol de carte. */
  restaurer() {
    const carte = this.closest("product-card");
    if (!carte || !window.CadenceApercu) return;
    window.CadenceApercu.cacher(carte, window.CadenceApercu.NIVEAU_PASTILLE);
  }

  /**
   * Appui au doigt : bascule durablement la carte sur un autre coloris.
   * On superpose l’image plutôt que de réécrire le carrousel du thème :
   * moins invasif, et réversible d’un appui sur une autre pastille.
   */
  epingler(produit, tous) {
    const carte = this.closest('product-card');
    if (!carte) return;
    this.epingle = produit;
    window.CadenceApercu?.epingler(carte, produit.image_large || produit.image);

    /*
     * Le nom affiché doit suivre, sinon la carte annonce un coloris et en
     * montre un autre. Le titre n’a pas de classe exploitable et change de
     * balise selon le gabarit : on repère donc les éléments dont le texte
     * est exactement le titre courant, et on les remplace.
     */
    const precedent = tous.find((p) => p.handle === this.dataset.actuel);
    if (precedent && produit.titre && precedent.titre !== produit.titre) {
      carte.querySelectorAll('*').forEach((noeud) => {
        if (noeud.children.length === 0 && noeud.textContent.trim() === precedent.titre) {
          noeud.textContent = produit.titre;
        }
      });
    }

    // Sous le titre de la carte, le nom du coloris suit l'image.
    const nomCouleur = carte.querySelector('.carte-titre__couleur');
    if (nomCouleur && produit.couleur) nomCouleur.textContent = produit.couleur;

    // La carte entière mène désormais au coloris regardé.
    carte.querySelectorAll('a[href*="/products/"]').forEach((lien) => {
      lien.href = produit.url;
    });

    this.dataset.actuel = produit.handle;
    this.rendre(tous);
  }
}

if (!customElements.get('color-siblings')) {
  customElements.define('color-siblings', ColorSiblings);
}
